import { Button } from '@shared/components';
import { DeliveryRequest } from '@shared/sdk-v1/models';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { getCurrency } from '@state/courierSlice';
import { getCurrencySymbol } from '../../utils/currencyUtils';

export interface DeliveryRequestCardProps {
  request: DeliveryRequest;
  getPackageTrackingNumber: (packageId: string) => string;
  getPackageWeight: (packageId: string) => number;
  getPackageDescription: (packageId: string) => string;
  getPackageMerchant: (packageId: string) => string;
  getPackageValue: (packageId: string) => number;
  onEditDelivery: (id: number | undefined) => void;
  onCancelDelivery: (id: number | undefined) => void;
}

const DeliveryRequestCard: React.FC<DeliveryRequestCardProps> = ({
  request,
  getPackageTrackingNumber,
  getPackageWeight,
  getPackageDescription,
  getPackageMerchant,
  getPackageValue,
  onEditDelivery,
  onCancelDelivery,
}) => {
  const isPending = request.status?.toLowerCase() === 'pending';
  const [expanded, setExpanded] = useState(false);
  const currencySymbol = getCurrencySymbol(useSelector(getCurrency));
  const fmtMoney = (amount: number) =>
    `${currencySymbol}${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const togglePackageDetails = () => setExpanded((prev) => !prev);

  const getStatusClass = (status?: string): string => {
    switch (status) {
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Delivered':
        return 'bg-green-100 text-green-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const formattedDate = request.date ? new Date(request.date).toLocaleDateString() : '-';

  return (
    <div className="bg-white dark:bg-gray-800 dark:border-gray-700 border border-gray-200 rounded-lg p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
        <div className="flex items-center gap-3">
          {request.packages && request.packages.length > 0 && (
            <div className="w-8 h-8 bg-primary text-primary-text rounded-full flex items-center justify-center text-sm font-bold">
              {request.packages.length}
            </div>
          )}
          <div>
            <p className="font-semibold dark:text-white text-base">{request.recipientName || '-'}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{request.type || '-'}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">ID: {request.id ?? '-'}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${getStatusClass(
              request.status,
            )}`}
          >
            {request.status || 'Unknown'}
          </span>
          {request.packages && request.packages.length > 0 && (
            <button
              onClick={togglePackageDetails}
              aria-expanded={expanded}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              aria-label={expanded ? 'Collapse package details' : 'Expand package details'}
            >
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Basic Info */}
      <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
        <p>
          <strong>Location:</strong> {request.deliveryLocation || '-'}
        </p>
        <p>
          <strong>Date:</strong> {formattedDate}
        </p>
        <p>
          <strong>Time:</strong> {request.preferredTime || '-'}
        </p>
        {request.contactNumber && (
          <p>
            <strong>Contact:</strong> {request.contactNumber}
          </p>
        )}
        {request.amount !== undefined && (
          <p>
            <strong>Amount:</strong>{' '}
            {fmtMoney(request.amount)}
          </p>
        )}
      </div>

      {/* Notes Section - Displayed prominently if present */}
      {request.notes && (
        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <svg
              className="w-4 h-4 text-gray-500 dark:text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Notes:</span>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300">{request.notes}</p>
        </div>
      )}

      {/* Package Details Dropdown */}
      {expanded && request.packages && request.packages.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-medium text-background-text text-sm mb-3">Package Details</h4>
          <div className="space-y-3">
            {request.packages.map((packageId) => (
              <div key={packageId} className="bg-white dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm text-background-text">Package #{packageId}</span>
                  <span className="text-xs text-gray-500">
                    Tracking: {getPackageTrackingNumber(packageId)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div>
                    <span className="font-medium">Weight:</span> {getPackageWeight(packageId)} lbs
                  </div>
                  <div>
                    <span className="font-medium">Description:</span> {getPackageDescription(packageId)}
                  </div>
                  <div>
                    <span className="font-medium">Merchant:</span> {getPackageMerchant(packageId)}
                  </div>
                  <div>
                    <span className="font-medium">Value:</span>{' '}
                    {fmtMoney(getPackageValue(packageId))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons - Only show for pending requests */}
      {isPending && (
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <Button
            variant='secondary'
            onClick={() => onEditDelivery(request.id)}
          >
            Edit Delivery
          </Button>
          <Button
            variant='danger'
            onClick={() => onCancelDelivery(request.id)}
          >
            Cancel Delivery
          </Button>
        </div>
      )}
    </div>
  );
};

export default DeliveryRequestCard;
